import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  Users, Radio, Receipt, Ticket, LifeBuoy, Ribbon, Search, ShieldCheck,
  Trash2, CheckCircle2, Plus, TrendingUp, DollarSign, Mail, Send, Flag, Ban,
  X, UserPlus, Store, ShoppingBag, Ticket as TicketIcon, Calendar, Square, Trophy, Gift, Download, ThumbsUp, ThumbsDown, KeyRound,
} from "lucide-react";
import { api } from "@/lib/api";
import { Loading, PageHead, money } from "@/components/common";
import { StatusPill } from "@/components/StatusPill";

const TABS = ["Overview", "Users", "Shows", "Orders", "Referrals", "Invites", "Reports", "Deletions", "Support", "Waitlist"];

export default function Admin() {
  const [tab, setTab] = useState("Overview");
  return (
    <div className="fade-up">
      <PageHead title="Admin Dashboard" sub="Platform overview and management." />
      <div className="ss-tabs" data-testid="admin-tabs">
        {TABS.map((t) => (
          <button key={t} className={`ss-tab ${tab === t ? "active" : ""}`} onClick={() => setTab(t)} data-testid={`admin-tab-${t.toLowerCase()}`}>{t}</button>
        ))}
      </div>
      {tab === "Overview" && <Overview />}
      {tab === "Users" && <UsersTab />}
      {tab === "Shows" && <ShowsTab />}
      {tab === "Orders" && <OrdersTab />}
      {tab === "Referrals" && <ReferralsTab />}
      {tab === "Invites" && <InvitesTab />}
      {tab === "Reports" && <ReportsTab />}
      {tab === "Deletions" && <DeletionsTab />}
      {tab === "Support" && <SupportTab />}
      {tab === "Waitlist" && <WaitlistTab />}
    </div>
  );
}

function Stat({ icon: Icon, value, label, testid }) {
  return (
    <div className="ss-card stat-card" data-testid={testid}>
      <div className="stat-ic"><Icon size={22} /></div>
      <div><div className="stat-val">{value}</div><div className="stat-label">{label}</div></div>
    </div>
  );
}

function Overview() {
  const [ov, setOv] = useState(null);
  const [growth, setGrowth] = useState(null);
  useEffect(() => {
    Promise.all([api.get("/admin/overview"), api.get("/admin/growth", { days: 14 })])
      .then(([o, g]) => { setOv(o); setGrowth(g); }).catch(() => {});
  }, []);
  if (!ov || !growth) return <Loading />;

  const maxSign = Math.max(1, ...growth.signups.map((d) => d.count));
  const maxSales = Math.max(1, ...growth.sales.map((d) => d.revenue));

  return (
    <div>
      <div className="ss-grid grid-4" data-testid="admin-overview">
        <Stat icon={Users} value={ov.users.total} label="Total members" testid="stat-users" />
        <Stat icon={Ribbon} value={ov.users.founding_members} label={`Founding (${ov.founding_remaining} left)`} testid="stat-founding" />
        <Stat icon={Radio} value={ov.shows.live} label={`Live now / ${ov.shows.total} shows`} testid="stat-shows" />
        <Stat icon={DollarSign} value={money(ov.orders.revenue)} label={`Revenue · ${ov.orders.total} orders`} testid="stat-revenue" />
        <Stat icon={ShieldCheck} value={ov.users.verified_sellers} label="Verified sellers" />
        <Stat icon={Ticket} value={`${ov.invites.used}/${ov.invites.capacity}`} label="Invites used" />
        <Stat icon={LifeBuoy} value={ov.support.open} label={`Open tickets / ${ov.support.total}`} />
        <Stat icon={Users} value={ov.waitlist.pending} label={`Waitlist pending / ${ov.waitlist.total}`} />
      </div>

      <div className="ss-grid grid-2" style={{ marginTop: 20 }}>
        <div className="ss-card" style={{ padding: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 800, marginBottom: 16 }}><TrendingUp size={18} style={{ color: "var(--brand)" }} /> Signups (14d)</div>
          <div className="bars">
            {growth.signups.map((d) => (
              <div className="bar-col" key={d.date} title={`${d.date}: ${d.count}`}>
                <div className="bar" style={{ height: `${(d.count / maxSign) * 100}%`, background: "var(--brand)" }} />
              </div>
            ))}
          </div>
        </div>
        <div className="ss-card" style={{ padding: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 800, marginBottom: 16 }}><DollarSign size={18} style={{ color: "var(--gold-2)" }} /> Sales revenue (14d)</div>
          <div className="bars">
            {growth.sales.map((d) => (
              <div className="bar-col" key={d.date} title={`${d.date}: ${money(d.revenue)}`}>
                <div className="bar" style={{ height: `${(d.revenue / maxSales) * 100}%`, background: "var(--gold)" }} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function useSearchList(endpoint) {
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);
  const load = useCallback(async (query) => {
    const res = await api.get(endpoint, query ? { q: query } : undefined);
    setData(res);
  }, [endpoint]);
  useEffect(() => { load(""); }, [load]);
  return { q, setQ, data, reload: () => load(q), search: () => load(q) };
}

function SearchBar({ q, setQ, onSearch, placeholder }) {
  return (
    <form style={{ marginBottom: 16 }} onSubmit={(e) => { e.preventDefault(); onSearch(); }}>
      <div className="ss-field" style={{ maxWidth: 380 }}>
        <Search size={18} className="ss-muted" />
        <input placeholder={placeholder} value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-search" />
      </div>
    </form>
  );
}

function UsersTab() {
  const { q, setQ, data, reload, search } = useSearchList("/admin/users");
  const [detailId, setDetailId] = useState(null);
  if (!data) return <Loading />;

  const act = async (id, body) => {
    try { await api.patch(`/admin/users/${id}`, body); toast.success("Updated"); reload(); }
    catch (e) { toast.error(e.message); }
  };
  const remove = async (id) => {
    if (!window.confirm("Remove this user?")) return;
    try { await api.del(`/admin/users/${id}`); toast.success("Removed"); reload(); }
    catch (e) { toast.error(e.message); }
  };
  const banUser = async (id, name, isBanned) => {
    if (!isBanned && !window.confirm(`Suspend ${name}? They'll be logged out and can't sign in until unsuspended.`)) return;
    try { await api.patch(`/admin/users/${id}`, { is_banned: !isBanned }); toast.success(isBanned ? "Account restored" : "Account suspended"); reload(); }
    catch (e) { toast.error(e.message); }
  };
  const resetPw = async (id, name) => {
    const pw = window.prompt(`Set a new password for ${name} (min 6 characters). Share it with them privately — they can change it later.`);
    if (pw === null) return;
    if (pw.trim().length < 6) { toast.error("Password must be at least 6 characters"); return; }
    try { await api.post(`/admin/users/${id}/reset-password`, { password: pw.trim() }); toast.success(`Password reset for ${name}`); }
    catch (e) { toast.error(e.message); }
  };
  const exportCsv = async () => {
    try {
      const r = await api.get("/admin/users/export");
      const blob = new Blob([r.csv], { type: "text/csv" });
      const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = r.filename; a.click();
      toast.success(`Exported ${r.count} users`);
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <div style={{ flex: 1 }}><SearchBar q={q} setQ={setQ} onSearch={search} placeholder="Search name, email, store…" /></div>
        <button className="ss-btn ss-btn-ghost ss-btn-sm" onClick={exportCsv} data-testid="admin-users-export" style={{ marginBottom: 12 }}><Download size={14} /> Export CSV</button>
      </div>
      <div className="ss-card" style={{ overflow: "auto" }}>
        <table className="ss-table" data-testid="admin-users-table">
          <thead><tr><th>User</th><th>Flags</th><th>Followers</th><th>Invited</th><th style={{ textAlign: "right" }}>Actions</th></tr></thead>
          <tbody>
            {data.users.map((u) => (
              <tr key={u.user_id} data-testid={`admin-user-${u.user_id}`}>
                <td>
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}
                    onClick={() => setDetailId(u.user_id)}
                    data-testid={`open-user-${u.user_id}`}
                    title="View full details"
                  >
                    <img src={u.avatar} alt="" style={{ width: 34, height: 34, borderRadius: 999, objectFit: "cover" }} />
                    <div><div style={{ fontWeight: 700, color: "var(--brand)" }}>{u.name}</div><div className="ss-muted" style={{ fontSize: 12 }}>{u.email}</div></div>
                  </div>
                </td>
                <td>
                  <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                    {u.seller_verified && <span className="ss-pill pill--delivered">Verified</span>}
                    {u.founding && <span className="badge-founding"><Ribbon size={10} /> #{u.founding_number}</span>}
                    {u.is_admin && <span className="ss-pill pill--ended">Admin</span>}
                    {u.is_banned && <span className="ss-pill pill--open" style={{ background: "var(--red)", color: "#fff" }}>Suspended</span>}
                  </div>
                </td>
                <td>{u.followers}</td>
                <td>{u.invited}</td>
                <td style={{ textAlign: "right" }}>
                  <div style={{ display: "inline-flex", gap: 6 }}>
                    <button className="ss-btn ss-btn-sm ss-btn-ghost" onClick={() => act(u.user_id, { seller_verified: !u.seller_verified })} data-testid={`verify-${u.user_id}`} title="Toggle verified"><CheckCircle2 size={14} /></button>
                    <button className="ss-btn ss-btn-sm ss-btn-ghost" onClick={() => act(u.user_id, { is_admin: !u.is_admin })} data-testid={`admin-${u.user_id}`} title="Toggle admin"><ShieldCheck size={14} /></button>
                    <button className="ss-btn ss-btn-sm ss-btn-ghost" onClick={() => resetPw(u.user_id, u.name)} data-testid={`reset-pw-${u.user_id}`} title="Reset password"><KeyRound size={14} /></button>
                    <button className="ss-btn ss-btn-sm ss-btn-ghost" style={{ color: u.is_banned ? "var(--brand)" : "var(--red)" }} onClick={() => banUser(u.user_id, u.name, u.is_banned)} data-testid={`ban-${u.user_id}`} title={u.is_banned ? "Unsuspend" : "Suspend"}><Ban size={14} /></button>
                    <button className="ss-btn ss-btn-sm ss-btn-ghost" style={{ color: "var(--red)", borderColor: "var(--red)" }} onClick={() => remove(u.user_id)} data-testid={`remove-${u.user_id}`}><Trash2 size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {detailId && <UserDetailModal id={detailId} onClose={() => setDetailId(null)} />}
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "8px 0", borderBottom: "1px solid var(--line)" }}>
      <span className="ss-muted" style={{ fontSize: 13 }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: 600, textAlign: "right" }}>{value}</span>
    </div>
  );
}

function JoinBadge({ join }) {
  if (!join) return null;
  if (join.method === "invited") {
    return (
      <div className="ss-card" style={{ padding: 14, background: "var(--gold-soft)", display: "flex", gap: 10, alignItems: "center" }} data-testid="user-join-invited">
        <UserPlus size={18} style={{ color: "var(--brand)" }} />
        <div>
          <div style={{ fontWeight: 700, fontSize: 14 }}>Joined by invite</div>
          <div className="ss-muted" style={{ fontSize: 12 }}>
            Invited by <b>{join.inviter_name || "another member"}</b> · code <b>{join.invite_code_used}</b>
          </div>
        </div>
      </div>
    );
  }
  if (join.method === "public_code") {
    return (
      <div className="ss-card" style={{ padding: 14, background: "var(--gold-soft)", display: "flex", gap: 10, alignItems: "center" }} data-testid="user-join-public">
        <TicketIcon size={18} style={{ color: "var(--brand)" }} />
        <div>
          <div style={{ fontWeight: 700, fontSize: 14 }}>Registered with a public code</div>
          <div className="ss-muted" style={{ fontSize: 12 }}>
            Code <b>{join.invite_code_used}</b>{join.invite_label ? ` · ${join.invite_label}` : ""}
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="ss-card" style={{ padding: 14, background: "var(--surface-3)", display: "flex", gap: 10, alignItems: "center" }} data-testid="user-join-registered">
      <UserPlus size={18} style={{ color: "var(--brand)" }} />
      <div>
        <div style={{ fontWeight: 700, fontSize: 14 }}>Registered directly</div>
        <div className="ss-muted" style={{ fontSize: 12 }}>No invite code on record</div>
      </div>
    </div>
  );
}

function UserDetailModal({ id, onClose }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    api.get(`/admin/users/${id}`).then(setD).catch((e) => setErr(e.message));
  }, [id]);

  const joined = d?.created_at ? new Date(d.created_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }) : "—";

  return (
    <div className="ss-modal-back" onClick={onClose} data-testid="user-detail-modal">
      <div className="ss-modal" style={{ maxWidth: 560, position: "relative" }} onClick={(e) => e.stopPropagation()}>
        <button className="admin-modal-x" onClick={onClose} data-testid="user-detail-close" aria-label="Close"><X size={18} /></button>
        {!d && !err && <Loading />}
        {err && <div className="ss-muted" style={{ padding: 20 }}>{err}</div>}
        {d && (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
              <img src={d.avatar} alt="" style={{ width: 56, height: 56, borderRadius: 999, objectFit: "cover" }} />
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 800, fontSize: 18, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  {d.name}
                  {d.is_admin && <span className="ss-pill pill--ended">Admin</span>}
                  {d.founding && <span className="badge-founding"><Ribbon size={10} /> #{d.founding_number}</span>}
                  {d.is_banned && <span className="ss-pill" style={{ background: "var(--red)", color: "#fff" }}>Suspended</span>}
                </div>
                <div className="ss-muted" style={{ fontSize: 13 }}>{d.email}</div>
                {d.username && <div className="ss-muted" style={{ fontSize: 12 }}>@{d.username}</div>}
              </div>
            </div>

            <JoinBadge join={d.join} />

            <div className="ss-grid grid-3" data-testid="user-stat-grid">
              <div className="ss-card" style={{ padding: 12, textAlign: "center" }}><div style={{ fontWeight: 800, fontSize: 20, color: "var(--brand)" }}>{d.stats.orders_as_buyer}</div><div className="ss-muted" style={{ fontSize: 11 }}>Orders</div></div>
              <div className="ss-card" style={{ padding: 12, textAlign: "center" }}><div style={{ fontWeight: 800, fontSize: 20, color: "var(--brand)" }}>{d.stats.shows_hosted}</div><div className="ss-muted" style={{ fontSize: 11 }}>Shows hosted</div></div>
              <div className="ss-card" style={{ padding: 12, textAlign: "center" }}><div style={{ fontWeight: 800, fontSize: 20, color: "var(--brand)" }}>{d.stats.followers}</div><div className="ss-muted" style={{ fontSize: 11 }}>Followers</div></div>
              <div className="ss-card" style={{ padding: 12, textAlign: "center" }}><div style={{ fontWeight: 800, fontSize: 20, color: "var(--brand)" }}>{d.stats.following}</div><div className="ss-muted" style={{ fontSize: 11 }}>Following</div></div>
              <div className="ss-card" style={{ padding: 12, textAlign: "center" }}><div style={{ fontWeight: 800, fontSize: 20, color: "var(--brand)" }}>{d.stats.invited}</div><div className="ss-muted" style={{ fontSize: 11 }}>Members invited</div></div>
              <div className="ss-card" style={{ padding: 12, textAlign: "center" }}><div style={{ fontWeight: 800, fontSize: 20, color: "var(--brand)" }}>{d.own_invite_uses}</div><div className="ss-muted" style={{ fontSize: 11 }}>Own code uses</div></div>
            </div>

            <div>
              <InfoRow label="Joined" value={joined} />
              <InfoRow label="Account type" value={d.is_seller ? (d.seller_verified ? "Verified seller" : "Seller") : "Buyer"} />
              <InfoRow label="Email verified" value={d.email_verified ? "Yes" : "No"} />
              <InfoRow label="Phone" value={d.phone || "—"} />
              {d.store_name && <InfoRow label="Store" value={d.store_name} />}
              {d.own_invite_code && <InfoRow label="Their invite code" value={d.own_invite_code} />}
              <InfoRow label="User ID" value={<span style={{ fontFamily: "monospace", fontSize: 11 }}>{d.user_id}</span>} />
            </div>

            {d.bio && <div className="ss-muted" style={{ fontSize: 13, lineHeight: 1.6 }}>{d.bio}</div>}
          </div>
        )}
      </div>
    </div>
  );
}

function ReportsTab() {
  const [data, setData] = useState(null);
  const load = () => api.get("/admin/reports").then(setData).catch(() => setData({ reports: [] }));
  useEffect(() => { load(); }, []);
  if (!data) return <Loading />;

  const REASON_LABEL = { harassment: "Harassment/trolling", spam: "Spam or scam", inappropriate: "Inappropriate", impersonation: "Impersonation", other: "Other" };
  const setStatus = async (id, status) => {
    try { await api.patch(`/admin/reports/${id}`, { status }); toast.success("Updated"); load(); }
    catch (e) { toast.error(e.message); }
  };
  const banReported = async (id, name) => {
    if (!id) return toast.error("This report has no linked account");
    if (!window.confirm(`Suspend ${name}? They'll be logged out and can't sign in.`)) return;
    try { await api.patch(`/admin/users/${id}`, { is_banned: true }); toast.success("Account suspended"); load(); }
    catch (e) { toast.error(e.message); }
  };

  if (data.reports.length === 0)
    return <div className="ss-card" style={{ padding: 40, textAlign: "center" }} data-testid="reports-empty"><Flag size={26} className="ss-muted" /><div style={{ fontWeight: 800, marginTop: 8 }}>No reports</div><div className="ss-muted" style={{ fontSize: 14 }}>User reports will show up here.</div></div>;

  return (
    <div className="ss-card" style={{ overflow: "auto" }}>
      <table className="ss-table" data-testid="admin-reports-table">
        <thead><tr><th>Reported</th><th>Reason</th><th>Details</th><th>By</th><th>Status</th><th style={{ textAlign: "right" }}>Actions</th></tr></thead>
        <tbody>
          {data.reports.map((r) => (
            <tr key={r.id} data-testid={`report-row-${r.id}`}>
              <td>
                <div style={{ fontWeight: 700 }}>{r.reported_name}</div>
                <div className="ss-muted" style={{ fontSize: 12 }}>{r.context || "—"}{r.reported_is_banned && <span className="ss-pill pill--open" style={{ background: "var(--red)", color: "#fff", marginLeft: 6 }}>Suspended</span>}</div>
              </td>
              <td>{REASON_LABEL[r.reason] || r.reason}</td>
              <td className="ss-muted" style={{ maxWidth: 260, fontSize: 13 }}>{r.details || "—"}</td>
              <td className="ss-muted" style={{ fontSize: 13 }}>{r.reporter_name}</td>
              <td><StatusPill status={r.status} /></td>
              <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                <div style={{ display: "inline-flex", gap: 6 }}>
                  {r.reported_id && !r.reported_is_banned && <button className="ss-btn ss-btn-sm ss-btn-ghost" style={{ color: "var(--red)" }} onClick={() => banReported(r.reported_id, r.reported_name)} data-testid={`report-ban-${r.id}`} title="Suspend user"><Ban size={14} /></button>}
                  {r.status !== "actioned" && <button className="ss-btn ss-btn-sm ss-btn-ghost" onClick={() => setStatus(r.id, "actioned")} data-testid={`report-action-${r.id}`}>Actioned</button>}
                  {r.status !== "dismissed" && <button className="ss-btn ss-btn-sm ss-btn-ghost" onClick={() => setStatus(r.id, "dismissed")} data-testid={`report-dismiss-${r.id}`}>Dismiss</button>}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DeletionsTab() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = () => api.get("/admin/deletion-requests").then(setData).catch(() => setData({ requests: [] }));
  useEffect(() => { load(); }, []);
  if (!data) return <Loading />;

  const complete = async (id, email) => {
    if (!window.confirm(`Permanently delete the account for ${email}? This anonymizes their orders and removes personal data. This cannot be undone.`)) return;
    setBusy(id + "c");
    try { const r = await api.post(`/admin/deletion-requests/${id}/complete`); toast.success(r.account_deleted ? "Account deleted" : "Marked done (no matching account found)"); load(); }
    catch (e) { toast.error(e.message); } finally { setBusy(null); }
  };
  const dismiss = async (id) => {
    setBusy(id + "d");
    try { await api.post(`/admin/deletion-requests/${id}/dismiss`); toast.success("Dismissed"); load(); }
    catch (e) { toast.error(e.message); } finally { setBusy(null); }
  };

  if (data.requests.length === 0)
    return <div className="ss-card" style={{ padding: 40, textAlign: "center" }} data-testid="deletions-empty"><Trash2 size={26} className="ss-muted" /><div style={{ fontWeight: 800, marginTop: 8 }}>No deletion requests</div><div className="ss-muted" style={{ fontSize: 14 }}>Requests from the public delete-account page appear here.</div></div>;

  return (
    <div className="ss-card" style={{ overflow: "auto" }}>
      <table className="ss-table" data-testid="admin-deletions-table">
        <thead><tr><th>Email</th><th>Account</th><th>Reason</th><th>Requested</th><th>Status</th><th style={{ textAlign: "right" }}>Actions</th></tr></thead>
        <tbody>
          {data.requests.map((r) => (
            <tr key={r.id} data-testid={`deletion-row-${r.id}`}>
              <td style={{ fontWeight: 700 }}>{r.email}</td>
              <td>{r.user_found ? <span className="ss-pill pill--delivered">Found</span> : <span className="ss-pill pill--open">No account</span>}</td>
              <td className="ss-muted" style={{ maxWidth: 260, fontSize: 13 }}>{r.reason || "—"}</td>
              <td className="ss-muted" style={{ fontSize: 13 }}>{new Date(r.created_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}</td>
              <td><StatusPill status={r.status} /></td>
              <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                {r.status === "pending" ? (
                  <div style={{ display: "inline-flex", gap: 6 }}>
                    <button className="ss-btn ss-btn-sm ss-btn-ghost" style={{ color: "var(--red)", borderColor: "var(--red)" }} disabled={busy === r.id + "c"} onClick={() => complete(r.id, r.email)} data-testid={`deletion-complete-${r.id}`}><Trash2 size={14} /> Delete & complete</button>
                    <button className="ss-btn ss-btn-sm ss-btn-ghost" disabled={busy === r.id + "d"} onClick={() => dismiss(r.id)} data-testid={`deletion-dismiss-${r.id}`}>Dismiss</button>
                  </div>
                ) : <span className="ss-muted" style={{ fontSize: 13 }}>—</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ShowsTab() {
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = useCallback(async () => {
    const params = {};
    if (status) params.status = status;
    if (q.trim()) params.q = q.trim();
    setData(await api.get("/admin/shows", Object.keys(params).length ? params : undefined));
  }, [status, q]);
  useEffect(() => { load(); }, [status]); // eslint-disable-line
  const del = async (s) => {
    if (!window.confirm(`Delete "${s.title}" by ${s.seller_name}? This removes the show and its items.`)) return;
    setBusy(s.id);
    try { await api.del(`/shows/${s.id}`); toast.success("Show deleted"); load(); }
    catch (e) { toast.error(e.message); } finally { setBusy(null); }
  };
  const stop = async (s) => {
    if (!window.confirm(`Stop "${s.title}"? This ends the live show for all viewers.`)) return;
    setBusy(s.id);
    try { await api.post(`/shows/${s.id}/end`); toast.success("Show stopped"); load(); }
    catch (e) { toast.error(e.message); } finally { setBusy(null); }
  };
  const FILTERS = [["", "All"], ["live", "Live"], ["scheduled", "Scheduled"], ["ended", "Ended"]];
  if (!data) return <Loading />;
  return (
    <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
        {FILTERS.map(([v, lbl]) => (
          <button key={v || "all"} className={`ss-chip ${status === v ? "active" : ""}`} onClick={() => setStatus(v)} data-testid={`admin-shows-filter-${v || "all"}`}>{lbl}</button>
        ))}
      </div>
      <SearchBar q={q} setQ={setQ} onSearch={load} placeholder="Search title, seller, category…" />
      <div className="ss-card" style={{ overflow: "auto" }}>
        <table className="ss-table" data-testid="admin-shows-table">
          <thead><tr><th>Title</th><th>Seller</th><th>Category</th><th>Lots</th><th>Viewers</th><th>Status</th><th style={{ textAlign: "right" }}>Actions</th></tr></thead>
          <tbody>
            {data.shows.length === 0 && (
              <tr><td colSpan={7} style={{ textAlign: "center", padding: 24, color: "var(--muted)" }}>No shows found</td></tr>
            )}
            {data.shows.map((s) => (
              <tr key={s.id} data-testid={`admin-show-row-${s.id}`}>
                <td style={{ fontWeight: 700 }}>{s.title}</td>
                <td>{s.seller_name}</td>
                <td>{s.category}</td>
                <td>{s.lots}</td>
                <td>{s.viewer_count?.toLocaleString()}</td>
                <td><StatusPill status={s.status} /></td>
                <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                  {s.status === "live" && (
                    <button className="ss-btn ss-btn-sm ss-btn-ghost" style={{ marginRight: 6 }} disabled={busy === s.id} onClick={() => stop(s)} data-testid={`admin-stop-show-${s.id}`}><Square size={13} /> Stop</button>
                  )}
                  <button className="ss-btn ss-btn-sm ss-btn-ghost" style={{ color: "var(--red)" }} disabled={busy === s.id} onClick={() => del(s)} data-testid={`admin-delete-show-${s.id}`}><Trash2 size={13} /> Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function OrdersTab() {
  const { q, setQ, data, search } = useSearchList("/admin/orders");
  if (!data) return <Loading />;
  return (
    <div>
      <SearchBar q={q} setQ={setQ} onSearch={search} placeholder="Search item, buyer, seller, tracking…" />
      <div className="ss-card" style={{ overflow: "auto" }}>
        <table className="ss-table" data-testid="admin-orders-table">
          <thead><tr><th>Item</th><th>Buyer</th><th>Seller</th><th>Amount</th><th>Status</th></tr></thead>
          <tbody>
            {data.orders.map((o) => (
              <tr key={o.id}>
                <td style={{ fontWeight: 700 }}>{o.item_title}</td>
                <td>{o.buyer_name}</td>
                <td>{o.seller_name}</td>
                <td>{money(o.amount)}</td>
                <td><StatusPill status={o.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ReferralsTab() {
  const [data, setData] = useState(null);
  const [ptype, setPtype] = useState("cash");
  const [pvalue, setPvalue] = useState("");
  const [plabel, setPlabel] = useState("");
  const [mlabel, setMlabel] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/referrals").then((d) => {
    setData(d);
    const s = d.settings || {};
    setPtype(s.prize_type || "cash");
    setPvalue(s.prize_value != null ? String(s.prize_value) : "");
    setPlabel(s.prize_label || "");
    setMlabel(s.month_label || "");
  }).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!data) return <Loading />;

  const savePrize = async () => {
    setBusy(true);
    try {
      await api.put("/admin/referrals/prize", {
        prize_type: ptype,
        prize_value: ptype === "cash" ? (parseFloat(pvalue) || 0) : null,
        prize_label: plabel,
        month_label: mlabel || undefined,
      });
      toast.success("Prize saved");
      load();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const newMonth = async () => {
    if (!window.confirm("Start a new month? This locks in the current top 5 as winners (with the current prize) and resets the leaderboard to zero.")) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/referrals/new-month");
      toast.success(`Month closed — ${r.archived.winners.length} winner(s) recorded`);
      load();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const entries = data.entries || [];
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Prize editor */}
      <div className="ss-card" style={{ padding: 18 }} data-testid="admin-referral-prize-card">
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
          <Gift size={18} style={{ color: "var(--gold-2)" }} />
          <h3 style={{ fontSize: 17 }}>This month's prize</h3>
        </div>
        <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
          <button className={`ss-chip ${ptype === "cash" ? "active" : ""}`} onClick={() => setPtype("cash")} data-testid="prize-type-cash">💵 Cash</button>
          <button className={`ss-chip ${ptype === "product" ? "active" : ""}`} onClick={() => setPtype("product")} data-testid="prize-type-product">🎁 Product</button>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,2fr)", gap: 10 }}>
          {ptype === "cash" ? (
            <div>
              <label className="ss-label">Cash amount (USD)</label>
              <div className="ss-field" style={{ height: 46 }}><DollarSign size={15} className="ss-muted" /><input type="number" min="0" placeholder="100" value={pvalue} onChange={(e) => setPvalue(e.target.value)} data-testid="prize-value" /></div>
            </div>
          ) : (
            <div>
              <label className="ss-label">Month label</label>
              <div className="ss-field" style={{ height: 46 }}><input placeholder="August 2026" value={mlabel} onChange={(e) => setMlabel(e.target.value)} data-testid="prize-month" /></div>
            </div>
          )}
          <div>
            <label className="ss-label">{ptype === "cash" ? "Note (optional)" : "Product name / description"}</label>
            <div className="ss-field" style={{ height: 46 }}><input placeholder={ptype === "cash" ? "e.g. Top referrer bonus" : "e.g. Premium Oud gift set"} value={plabel} onChange={(e) => setPlabel(e.target.value)} data-testid="prize-label" /></div>
          </div>
        </div>
        {ptype === "cash" && (
          <div style={{ marginTop: 10 }}>
            <label className="ss-label">Month label</label>
            <div className="ss-field" style={{ height: 46, maxWidth: 260 }}><input placeholder="August 2026" value={mlabel} onChange={(e) => setMlabel(e.target.value)} data-testid="prize-month-cash" /></div>
          </div>
        )}
        <div style={{ display: "flex", gap: 10, marginTop: 14, flexWrap: "wrap" }}>
          <button className="ss-btn ss-btn-primary ss-btn-sm" onClick={savePrize} disabled={busy} data-testid="prize-save">Save prize</button>
          <button className="ss-btn ss-btn-gold ss-btn-sm" onClick={newMonth} disabled={busy} data-testid="referral-new-month"><Calendar size={14} /> Start new month (lock winners & reset)</button>
        </div>
      </div>

      {/* Current leaderboard */}
      <div className="ss-card" style={{ overflow: "auto" }}>
        <div style={{ padding: "14px 16px 0", display: "flex", alignItems: "center", gap: 8 }}>
          <Trophy size={18} style={{ color: "var(--gold-2)" }} />
          <h3 style={{ fontSize: 17 }}>Current leaderboard — {data.settings?.month_label}</h3>
        </div>
        <table className="ss-table" data-testid="admin-referral-leaderboard" style={{ marginTop: 10 }}>
          <thead><tr><th style={{ width: 60 }}>Rank</th><th>Member</th><th>Qualified referrals</th><th>Pending</th></tr></thead>
          <tbody>
            {entries.length === 0 && <tr><td colSpan={4} style={{ textAlign: "center", padding: 24, color: "var(--muted)" }}>No qualified referrals yet this month</td></tr>}
            {entries.map((e) => (
              <tr key={e.user_id} data-testid={`admin-referral-row-${e.user_id}`} style={e.rank <= 5 ? { background: "var(--gold-soft)" } : undefined}>
                <td style={{ fontWeight: 800 }}>{e.rank <= 5 ? `🏆 ${e.rank}` : e.rank}</td>
                <td style={{ fontWeight: 700 }}>{e.name}{e.username ? <span className="ss-muted" style={{ fontWeight: 500 }}> @{e.username}</span> : null}</td>
                <td style={{ fontWeight: 800, color: "var(--brand)" }}>{e.count}</td>
                <td className="ss-muted">{e.pending || 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Past winners */}
      {(data.past_months || []).length > 0 && (
        <div className="ss-card" style={{ padding: 18 }} data-testid="admin-referral-past">
          <h3 style={{ fontSize: 17, marginBottom: 12 }}>Past months</h3>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {data.past_months.map((m) => (
              <div key={m.id} style={{ borderLeft: "3px solid var(--gold)", paddingLeft: 12 }}>
                <div style={{ fontWeight: 800 }}>{m.month_label} <span className="ss-muted" style={{ fontWeight: 500, fontSize: 13 }}>· {prizeText(m.prize) || "No prize set"}</span></div>
                <div className="ss-muted" style={{ fontSize: 13, marginTop: 2 }}>
                  {(m.winners || []).map((w) => `#${w.rank} ${w.name} (${w.count})`).join(" · ") || "No winners"}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function prizeText(p) {
  if (!p) return "";
  if (p.type === "cash" && p.value) return `$${Number(p.value).toFixed(0)} cash${p.label ? ` — ${p.label}` : ""}`;
  if (p.type === "product" && p.label) return p.label;
  return p.label || "";
}

function InvitesTab() {
  const [data, setData] = useState(null);
  const [count, setCount] = useState(10);
  const [maxUses, setMaxUses] = useState(1);
  const [inviteEmail, setInviteEmail] = useState("");
  const [emailLabel, setEmailLabel] = useState("");
  const [inviteMessage, setInviteMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [resending, setResending] = useState(null);
  const load = () => api.get("/admin/invites").then(setData).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!data) return <Loading />;

  const generate = async () => {
    try {
      const res = await api.post("/admin/invites/generate", { count: Number(count), max_uses: Number(maxUses) });
      toast.success(`Generated ${res.created_count} codes`);
      load();
    } catch (e) { toast.error(e.message); }
  };

  const emailInvite = async () => {
    const emails = inviteEmail.split(/[\s,;]+/).map((e) => e.trim()).filter(Boolean);
    if (emails.length === 0) return toast.error("Enter at least one email address");
    setSending(true);
    try {
      const res = await api.post("/admin/invites/email-bulk", {
        emails, max_uses: Number(maxUses) || 1,
        label: emailLabel.trim() || null, message: inviteMessage.trim() || null,
        origin: window.location.origin,
      });
      const msg = `Emailed ${res.sent} of ${res.total} invite${res.total === 1 ? "" : "s"}`
        + (res.failed ? ` · ${res.failed} failed to send` : "")
        + (res.invalid?.length ? ` · ${res.invalid.length} invalid skipped` : "");
      (res.failed || res.invalid?.length) ? toast.warning(msg) : toast.success(msg);
      setInviteEmail(""); setEmailLabel(""); setInviteMessage("");
      load();
    } catch (e) { toast.error(e.message); } finally { setSending(false); }
  };

  const resendInvite = async (code) => {
    setResending(code);
    try {
      const res = await api.post(`/admin/invites/${code}/resend`, { origin: window.location.origin });
      res.email_sent ? toast.success(`Resent ${code} to ${res.email}`) : toast.warning(`Couldn't deliver ${code} — check email settings`);
      load();
    } catch (e) { toast.error(e.message); } finally { setResending(null); }
  };

  const deleteCode = async (code) => {
    if (!window.confirm(`Delete invite code ${code}? This can't be undone.`)) return;
    try {
      await api.del(`/admin/invites/${code}`);
      toast.success(`Deleted ${code}`);
      load();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div>
      <div className="ss-card" style={{ padding: 18, display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap", marginBottom: 12 }}>
        <div><label className="ss-label">How many</label><div className="ss-field" style={{ width: 110, height: 44 }}><input type="number" value={count} onChange={(e) => setCount(e.target.value)} data-testid="invite-count" /></div></div>
        <div><label className="ss-label">Uses each</label><div className="ss-field" style={{ width: 110, height: 44 }}><input type="number" value={maxUses} onChange={(e) => setMaxUses(e.target.value)} data-testid="invite-maxuses" /></div></div>
        <button className="ss-btn ss-btn-primary" onClick={generate} data-testid="generate-invites"><Plus size={16} /> Generate codes</button>
      </div>

      <div className="ss-card" style={{ padding: 18, display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap", marginBottom: 16 }}>
        <div style={{ flex: 1, minWidth: 260 }}>
          <label className="ss-label">Invite by email <span className="ss-muted" style={{ fontWeight: 400 }}>· paste multiple, separated by commas or new lines</span></label>
          <textarea placeholder="person@example.com, another@example.com&#10;or one per line…" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} data-testid="invite-email-input"
            style={{ width: "100%", minHeight: 68, resize: "vertical", padding: "10px 12px", borderRadius: 12, border: "1px solid var(--line)", background: "var(--surface-2)", color: "var(--text)", fontSize: 14, fontFamily: "inherit" }} />
        </div>
        <div style={{ minWidth: 160 }}>
          <label className="ss-label">Label (optional)</label>
          <div className="ss-field" style={{ height: 44 }}><input placeholder="e.g. Launch seller" value={emailLabel} onChange={(e) => setEmailLabel(e.target.value)} data-testid="invite-email-label" /></div>
        </div>
        <div style={{ flexBasis: "100%" }}>
          <label className="ss-label">Personal message (optional)</label>
          <textarea placeholder="Add a short note — e.g. 'Loved your abayas at the market, would love you on SouqStream!'" value={inviteMessage} onChange={(e) => setInviteMessage(e.target.value)} data-testid="invite-message-input" maxLength={500}
            style={{ width: "100%", minHeight: 68, resize: "vertical", padding: "10px 12px", borderRadius: 12, border: "1px solid var(--line)", background: "var(--surface-2)", color: "var(--text)", fontSize: 14, fontFamily: "inherit" }} />
        </div>
        <button className="ss-btn ss-btn-primary" onClick={emailInvite} disabled={sending} data-testid="email-invite-button"><Send size={16} /> {sending ? "Sending…" : "Generate & email codes"}</button>
      </div>

      <div className="ss-card" style={{ overflow: "auto" }}>
        <table className="ss-table" data-testid="admin-invites-table">
          <thead><tr><th>Code</th><th>Owner</th><th>Sent to</th><th>Status</th><th>Used</th><th>Label</th><th style={{ textAlign: "right" }}>Actions</th></tr></thead>
          <tbody>
            {data.invites.map((c) => {
              const redeemer = c.redeemers && c.redeemers[0];
              const statusPill = {
                redeemed: { cls: "pill--delivered", label: "Redeemed" },
                used_up: { cls: "pill--delivered", label: "Fully used" },
                pending: { cls: "pill--open", label: "Sent · pending" },
                unused: { cls: "", label: "Unused" },
              }[c.status] || { cls: "", label: c.status };
              return (
              <tr key={c.code} data-testid={`invite-row-${c.code}`}>
                <td style={{ fontWeight: 800, letterSpacing: 1 }}>{c.code}</td>
                <td>{c.owner_name}</td>
                <td className="ss-muted">
                  {c.sent_to || "—"}
                  {c.sent_to && c.email_status === "failed" && <span className="ss-pill pill--open" style={{ marginLeft: 6 }}>email failed</span>}
                </td>
                <td data-testid={`invite-status-${c.code}`}>
                  <span className={`ss-pill ${statusPill.cls}`}>{statusPill.label}</span>
                  {redeemer && (redeemer.name || redeemer.email) && (
                    <div className="ss-muted" style={{ fontSize: 11, marginTop: 3 }}>
                      by {redeemer.name || redeemer.email}{c.redemptions > 1 ? ` +${c.redemptions - 1}` : ""}
                    </div>
                  )}
                </td>
                <td>{c.use_count}/{c.max_uses}</td>
                <td className="ss-muted">{c.label || "—"}</td>
                <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                  {c.status === "pending" && (
                    <button className="ss-btn ss-btn-sm ss-btn-ghost" onClick={() => resendInvite(c.code)} disabled={resending === c.code} data-testid={`resend-invite-${c.code}`} title="Resend invite email" style={{ marginRight: 6 }}>
                      <Send size={14} /> {resending === c.code ? "…" : "Resend"}
                    </button>
                  )}
                  <button className="ss-btn ss-btn-sm ss-btn-ghost" onClick={() => deleteCode(c.code)} data-testid={`delete-invite-${c.code}`} title="Delete code"><Trash2 size={15} /></button>
                </td>
              </tr>
            );})}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SupportTab() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("");
  const [reply, setReply] = useState({});
  const [agentName, setAgentName] = useState("");
  const load = (status) => api.get("/admin/support", status ? { status } : undefined).then(setData).catch(() => {});
  useEffect(() => { load(filter); /* eslint-disable-next-line */ }, [filter]);
  if (!data) return <Loading />;

  const sendReply = async (id) => {
    const text = (reply[id] || "").trim();
    if (text.length < 2) return toast.error("Reply can't be empty");
    try { await api.post(`/admin/support/${id}/reply`, { message: text, agent_name: agentName.trim() || undefined }); toast.success("Reply sent"); setReply((r) => ({ ...r, [id]: "" })); load(filter); }
    catch (e) { toast.error(e.message); }
  };
  const setStatus = async (id, status) => {
    try { await api.post(`/admin/support/${id}/status`, { status }); load(filter); } catch (e) { toast.error(e.message); }
  };

  return (
    <div>
      <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
        {["", "open", "answered", "resolved"].map((s) => (
          <button key={s || "all"} className={`ss-chip ${filter === s ? "active" : ""}`} onClick={() => setFilter(s)} data-testid={`support-filter-${s || "all"}`}>
            {s === "" ? `All (${data.stats.total})` : `${s} (${data.stats[s]})`}
          </button>
        ))}
      </div>
      <div style={{ marginBottom: 16, maxWidth: 340 }}>
        <label className="ss-label">Reply as (optional)</label>
        <div className="ss-field" style={{ height: 44 }}>
          <Send size={15} className="ss-muted" />
          <input placeholder="e.g. Amira — blank shows 'SouqStream Support'" value={agentName} onChange={(e) => setAgentName(e.target.value)} data-testid="support-agent-name" />
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }} data-testid="admin-support-list">
        {data.tickets.map((t) => (
          <div className="ss-card" style={{ padding: 18 }} key={t.id} data-testid={`ticket-${t.id}`}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
              <div>
                <div style={{ fontWeight: 800 }}>{t.subject}</div>
                <div className="ss-muted" style={{ fontSize: 13 }}>{t.name || "Guest"} · {t.email || "no email"}</div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {t.rating && (
                  <span data-testid={`ticket-rating-${t.id}`} title={t.rating === "up" ? "Rated helpful" : "Rated not helpful"} style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12, fontWeight: 800, padding: "3px 9px", borderRadius: 999, background: t.rating === "up" ? "rgba(16,185,129,.14)" : "rgba(220,38,38,.14)", color: t.rating === "up" ? "var(--success,#059669)" : "var(--danger,#dc2626)" }}>
                    {t.rating === "up" ? <ThumbsUp size={12} /> : <ThumbsDown size={12} />} {t.rating === "up" ? "Helpful" : "Not helpful"}
                  </span>
                )}
                <StatusPill status={t.status} />
              </div>
            </div>
            <p style={{ marginTop: 10, lineHeight: 1.5 }}>{t.message}</p>
            {(t.replies || []).map((r, i) => {
              const isAdmin = r.from === "admin" || r.admin_id;
              return (
                <div key={i} style={{ marginTop: 10, background: isAdmin ? "var(--gold-soft)" : "var(--surface-3)", borderRadius: 12, padding: 12 }}>
                  <div style={{ fontWeight: 800, color: isAdmin ? "var(--gold-2)" : "var(--brand)", fontSize: 13 }}>{isAdmin ? (r.admin_name || "SouqStream Support") : (r.name || t.name || "Customer")}</div>
                  <div style={{ marginTop: 4 }}>{r.message}</div>
                </div>
              );
            })}
            <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
              <div className="ss-field" style={{ flex: 1, minWidth: 220, height: 44 }}>
                <input placeholder="Type a reply…" value={reply[t.id] || ""} onChange={(e) => setReply((r) => ({ ...r, [t.id]: e.target.value }))} data-testid={`reply-input-${t.id}`} />
              </div>
              <button className="ss-btn ss-btn-sm ss-btn-primary" onClick={() => sendReply(t.id)} data-testid={`reply-send-${t.id}`}>Reply</button>
              <button className="ss-btn ss-btn-sm ss-btn-ghost" onClick={() => setStatus(t.id, "resolved")} data-testid={`resolve-${t.id}`}>Resolve</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function WaitlistTab() {
  const [data, setData] = useState(null);
  const [bulk, setBulk] = useState(10);
  const load = () => api.get("/admin/waitlist").then(setData).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!data) return <Loading />;

  const invite = async (id) => {
    try {
      const res = await api.post("/admin/waitlist/invite", { id, origin: window.location.origin });
      toast.success(res.email_sent ? `Invited · code ${res.code} · email sent` : `Invited · code ${res.code} · ⚠️ email failed`);
      load();
    } catch (e) { toast.error(e.message); }
  };
  const resend = async (id) => {
    try {
      const res = await api.post("/admin/waitlist/resend", { id, origin: window.location.origin });
      toast.success(res.email_sent ? `Invite email resent · code ${res.code}` : `⚠️ Resend failed · code ${res.code}`);
      load();
    } catch (e) { toast.error(e.message); }
  };
  const inviteBulk = async () => {
    try {
      const res = await api.post("/admin/waitlist/invite-bulk", { count: Number(bulk), origin: window.location.origin });
      toast.success(`Invited ${res.invited_count} people · ${res.email_sent_count} emails sent`);
      load();
    } catch (e) { toast.error(e.message); }
  };
  const removeOne = async (id, name) => {
    if (!window.confirm(`Remove ${name || "this signup"} from the waitlist?`)) return;
    try { await api.del(`/admin/waitlist/${id}`); toast.success("Removed from waitlist"); load(); }
    catch (e) { toast.error(e.message); }
  };

  return (
    <div data-testid="admin-waitlist-tab">
      <div className="ss-grid grid-3" style={{ marginBottom: 16 }}>
        <div className="ss-card stat-card"><div className="stat-ic"><Users size={22} /></div><div><div className="stat-val">{data.stats.total}</div><div className="stat-label">Total on waitlist</div></div></div>
        <div className="ss-card stat-card"><div className="stat-ic"><Mail size={22} /></div><div><div className="stat-val">{data.stats.pending}</div><div className="stat-label">Pending</div></div></div>
        <div className="ss-card stat-card"><div className="stat-ic"><Send size={22} /></div><div><div className="stat-val">{data.stats.invited}</div><div className="stat-label">Invited</div></div></div>
      </div>
      <div className="ss-card" style={{ padding: 16, display: "flex", gap: 12, alignItems: "flex-end", marginBottom: 16, flexWrap: "wrap" }}>
        <div><label className="ss-label">Bulk invite next N</label><div className="ss-field" style={{ width: 120, height: 44 }}><input type="number" value={bulk} onChange={(e) => setBulk(e.target.value)} data-testid="admin-bulk-count" /></div></div>
        <button className="ss-btn ss-btn-primary" onClick={inviteBulk} data-testid="admin-bulk-invite"><Send size={16} /> Invite next {bulk}</button>
      </div>
      {data.waitlist.length === 0 ? (
        <div className="ss-muted" style={{ padding: 20 }}>Waitlist is empty.</div>
      ) : (
        <div className="ss-card" style={{ overflow: "auto" }}>
          <table className="ss-table" data-testid="admin-waitlist-table">
            <thead><tr><th>Name</th><th>Email</th><th>Status</th><th style={{ textAlign: "right" }}>Action</th></tr></thead>
            <tbody>
              {data.waitlist.map((w) => (
                <tr key={w.id}>
                  <td style={{ fontWeight: 700 }}>{w.name}</td>
                  <td>{w.email}</td>
                  <td>{w.invited ? <span className="ss-pill pill--delivered">Invited</span> : <span className="ss-pill pill--open">Pending</span>}</td>
                  <td style={{ textAlign: "right" }}>
                    {!w.invited && <button className="ss-btn ss-btn-sm ss-btn-primary" onClick={() => invite(w.id)} data-testid={`admin-invite-${w.id}`}>Invite</button>}
                    {w.invited && (
                      <div style={{ display: "inline-flex", alignItems: "center", gap: 10, justifyContent: "flex-end" }}>
                        <span className="ss-muted" style={{ fontWeight: 800 }} data-testid={`admin-code-${w.id}`}>{w.invite_code}</span>
                        {w.email_opened
                          ? <span className="ss-pill" style={{ background: "var(--gold-soft)", color: "var(--brand)" }} data-testid={`admin-email-status-${w.id}`}>✓ Opened</span>
                          : w.email_status === "failed"
                            ? <span className="ss-pill pill--open" data-testid={`admin-email-status-${w.id}`}>Failed</span>
                            : <span className="ss-pill" style={{ background: "var(--surface-3)", color: "var(--muted)" }} data-testid={`admin-email-status-${w.id}`}>Sent</span>}
                        <button className="ss-btn ss-btn-sm ss-btn-ghost" onClick={() => resend(w.id)} data-testid={`admin-resend-${w.id}`}><Send size={14} /> Resend</button>
                      </div>
                    )}
                    <button className="ss-btn ss-btn-sm ss-btn-ghost" style={{ color: "var(--red)", marginLeft: 8 }} onClick={() => removeOne(w.id, w.name)} data-testid={`admin-delete-${w.id}`} title="Remove from waitlist"><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
